import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";
import "../../styles/components/transactionPrintLayouts/MRInvoiceLayout.css";

const normalizeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const toTitle = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  const unwrapValue = (candidate) => {
    if (candidate === undefined || candidate === null) return "";
    if (typeof candidate !== "object") return candidate;

    const valueCandidate =
      candidate.value ??
      candidate.input_value ??
      candidate.label ??
      candidate.name ??
      candidate.text;

    if (
      valueCandidate !== undefined &&
      valueCandidate !== null &&
      String(valueCandidate).trim() !== ""
    ) {
      return valueCandidate;
    }

    return "";
  };

  const sourcesToCheck = [
    source,
    source.data,
    source.values,
    source.field_values,
    source.custom_fields,
    source.item,
    source.row,
  ].filter(Boolean);

  for (const key of keys) {
    let value = "";

    for (const candidateSource of sourcesToCheck) {
      const direct = unwrapValue(candidateSource[key]);
      if (direct !== "") {
        value = direct;
        break;
      }

      const candidateSourceKeys = Object.keys(candidateSource || {});
      const normalizedTargetKey = normalizeKey(key);
      const matchedKey = candidateSourceKeys.find(
        (sourceKey) => normalizeKey(sourceKey) === normalizedTargetKey,
      );

      if (matchedKey) {
        const matchedValue = unwrapValue(candidateSource[matchedKey]);
        if (matchedValue !== "") {
          value = matchedValue;
          break;
        }
      }
    }

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getFieldOrderWeight = (field) => {
  const normalized = `${normalizeKey(field?.field_name)} ${normalizeKey(field?.title)}`;

  if (normalized.includes("description")) return 1;
  if (normalized.includes("specification") || normalized.includes("spec"))
    return 2;
  if (normalized.includes("qty") || normalized.includes("quantity")) return 3;
  if (normalized.includes("uom") || normalized.includes("unit")) return 4;
  if (normalized.includes("made") || normalized.includes("make")) return 5;
  if (normalized.includes("required") && normalized.includes("date")) return 6;
  if (normalized.includes("remark") || normalized.includes("narration"))
    return 7;

  return 100;
};

const isDescriptionField = (field) => {
  const normalized = `${normalizeKey(field?.field_name)} ${normalizeKey(field?.title)}`;
  return normalized.includes("description");
};

const getFieldColumnClass = (field) => {
  const normalized = `${normalizeKey(field?.field_name)} ${normalizeKey(field?.title)}`;

  if (normalized.includes("description")) return "mr-col-description";
  if (normalized.includes("specification") || normalized.includes("spec")) {
    return "mr-col-specification";
  }
  if (normalized.includes("qty") || normalized.includes("quantity")) {
    return "mr-col-qty";
  }
  if (normalized.includes("uom") || normalized.includes("unit")) {
    return "mr-col-uom";
  }
  if (normalized.includes("made") || normalized.includes("make")) {
    return "mr-col-made";
  }
  if (normalized.includes("required") && normalized.includes("date")) {
    return "mr-col-required-date";
  }
  if (normalized.includes("remark") || normalized.includes("narration")) {
    return "mr-col-remarks";
  }

  return "";
};

export const MRInvoiceLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
  rowData = [],
  invoiceTableData,
  selectedReportName,
}) => {
  let organizationData = {};
  try {
    organizationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organizationData = {};
  }

  const backendFields = (
    Array.isArray(rowData) && rowData.length
      ? rowData.filter((field) => !field.is_internal_field && field.field_name)
      : Object.keys((Array.isArray(itemDetails) && itemDetails[0]) || {}).map(
          (key) => ({
            field_name: key,
            title: toTitle(key),
          }),
        )
  ).map((field) => ({
    field_name: field.field_name,
    title: field.title || field.display_name || toTitle(field.field_name),
  }));

  const orderedFields = [...backendFields].sort((a, b) => {
    const weightDiff = getFieldOrderWeight(a) - getFieldOrderWeight(b);
    if (weightDiff !== 0) return weightDiff;
    return String(a.title || a.field_name || "").localeCompare(
      String(b.title || b.field_name || ""),
    );
  });

  const hasRequiredDateField = orderedFields.some(
    (field) => normalizeKey(field.field_name) === "requireddate",
  );
  const hasNarrationField = orderedFields.some(
    (field) => normalizeKey(field.field_name) === "narration",
  );

  if (!hasRequiredDateField) {
    orderedFields.push({
      field_name: "required_date",
      title: "MATERIAL REQUIRED DATE",
      isVirtual: true,
    });
  }

  if (!hasNarrationField) {
    orderedFields.push({
      field_name: "narration",
      title: "REMARKS",
      isVirtual: true,
    });
  }

  const requestDate = formatDate(
    pickValue(headDetails, ["date", "created_at", "request_date", "tran_date"]),
  );
  const materialRequestNo = pickValue(headDetails, [
    "tran_no",
    "material_request_no",
  ]);
  const materialRequiredDate = formatDate(
    pickValue(headDetails, [
      "required_date",
      "req_date",
      "material_required_date",
    ]),
  );
  const saleWorkOrder = pickValue(headDetails, [
    "workorder_no",
    "wo_no",
    "work_order_no",
  ]);

  const customerName = pickValue(
    headDetails,
    ["customer_name", "party_name", "company_name", "customer"],
    companyData.company_name || "",
  );

  const preparedBy = pickValue(headDetails, ["prepared_by", "requested_by"]);
  const preparedDate = formatDate(pickValue(headDetails, ["prepared_date"]));

  const approvedBy = pickValue(headDetails, ["approved_by"]);
  const approvedDate = formatDate(pickValue(headDetails, ["approved_at"]));
  const narration = pickValue(headDetails, ["narration", "remark"]);

  const formatCompanyAddress = (data) => {
    if (!data) return "N/A";
    const parts = [data.address?.trim(), data.city?.trim()].filter(Boolean);
    const pin = data.pincode ? `- ${data.pincode}` : "";
    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  const rows = Array.isArray(itemDetails)
    ? itemDetails.map((item, index) => ({
        slNo: index + 1,
        values: orderedFields.reduce((acc, field) => {
          const fieldName = field.field_name;
          const normalizedFieldName = normalizeKey(fieldName);

          let value = pickValue(item, [fieldName], "");

          if (normalizedFieldName === "narration") {
            value =
              pickValue(item, ["narration"], "") ||
              pickValue(headDetails, ["narration"], "");
          }

          if (normalizedFieldName === "requireddate") {
            value =
              pickValue(item, ["required_date"], "") ||
              pickValue(headDetails, ["required_date"], "");
            value = formatDate(value);
          }

          acc[fieldName] = value;
          return acc;
        }, {}),
      }))
    : [];

  const mrDownloadRows = rows.map((row) =>
    orderedFields.reduce((acc, field) => {
      acc.sl_no = row.slNo;
      acc[field.field_name] = row.values?.[field.field_name] || "";
      return acc;
    }, {}),
  );

  const mrTableData = {
    headings: [
      { display_name: "SL.NO", field_name: "sl_no", is_internal: false },
      ...orderedFields.map((field) => ({
        display_name: field.title || toTitle(field.field_name),
        field_name: field.field_name,
        is_internal: false,
      })),
    ],
    rows: mrDownloadRows,
  };

  return (
    <div className="invoice-container workorder-layout mr-workorder-layout mr-page-container">
      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData)}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
            </p>
          </div>
        </div>

        <div className="invoice-center">
          <h2 className="invoice-title">MATERIAL REQUIREMENT FORM</h2>
        </div>

        <div className="invoice-right">
          {[
            { label: "Ref No", value: "FP/MR/01" },
            { label: "Revision No", value: "01" },
            { label: "Revision Date", value: "06/06/2026" },
          ].map((item, index) => (
            <div key={index} className="purchase-detail">
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="workorder-header-row workorder-meta-row"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        }}
      >
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Material Request No:</span>
          <span
            className="workorder-meta-value"
            style={{ whiteSpace: "nowrap" }}
          >
            {materialRequestNo || "-"}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Request Date:</span>
          <span className="workorder-meta-value workorder-date-value">
            {requestDate || "-"}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Required Date:</span>
          <span className="workorder-meta-value workorder-date-value">
            {materialRequiredDate || "-"}
          </span>
        </div>
      </div>

      <div
        className="workorder-header-row workorder-meta-row"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
          gridTemplateColumns: "1fr 2fr",
        }}
      >
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Work Order No:</span>
          <span
            className="workorder-meta-value"
            style={{ whiteSpace: "nowrap" }}
          >
            {saleWorkOrder || "-"}
          </span>
        </div>
        <div className="workorder-meta-cell">
          <span className="workorder-meta-label">Customer Name:</span>
          <span className="workorder-meta-value">{customerName || "-"}</span>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll mr-table-scroll">
        <table className="invoice-table mr-table-workorder">
          <thead className="invoice-thead">
            <tr>
              <th
                className="invoice-th mr-col-sl"
                style={{ borderLeftWidth: "0px" }}
              >
                SL.NO
              </th>
              {orderedFields.map((field, index) => (
                <th
                  key={field.field_name}
                  className={`invoice-th ${getFieldColumnClass(field)}`.trim()}
                  style={
                    index === orderedFields.length - 1
                      ? { borderRightWidth: "0px" }
                      : {}
                  }
                >
                  {field.title || toTitle(field.field_name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.slNo}>
                <td
                  className="invoice-td mr-col-sl"
                  style={{ borderLeftWidth: "0px" }}
                >
                  {row.slNo}
                </td>
                {orderedFields.map((field, idx) => (
                  <td
                    key={`${row.slNo}-${field.field_name}`}
                    className={`invoice-td ${getFieldColumnClass(field)}${isDescriptionField(field) ? " mr-description-cell" : ""}`.trim()}
                    style={
                      idx === orderedFields.length - 1
                        ? { borderRightWidth: "0px" }
                        : {}
                    }
                  >
                    {row.values?.[field.field_name] || ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="workorder-footer-grid"
        style={{
          borderLeft: "1px solid #9ca3af",
          borderRight: "1px solid #9ca3af",
        }}
      >
        <div className="workorder-prepared-block">
          <div className="workorder-footer-title">Prepared By:</div>
          <div className="workorder-footer-row">
            <span className="workorder-footer-label">Name:</span>
            <span className="workorder-footer-value">{preparedBy || "-"}</span>
          </div>
          <div className="workorder-footer-row">
            <span className="workorder-footer-label">Date:</span>
            <span className="workorder-footer-value">
              {preparedDate || "-"}
            </span>
          </div>
        </div>
        <div className="workorder-approved-block">
          <div className="workorder-footer-title">Approved by:</div>
          <div className="workorder-footer-row">
            <span className="workorder-footer-label">Name:</span>
            <span className="workorder-footer-value">{approvedBy || "-"}</span>
          </div>
          <div className="workorder-footer-row">
            <span className="workorder-footer-label">Date:</span>
            <span className="workorder-footer-value">
              {approvedDate || "-"}
            </span>
          </div>
        </div>
      </div>

      {narration ? (
        <div
          className="workorder-header-row workorder-meta-row"
          style={{
            borderLeft: "1px solid #9ca3af",
            borderRight: "1px solid #9ca3af",
            borderBottom: "1px solid #9ca3af",
            gridTemplateColumns: "1fr",
          }}
        >
          <div className="workorder-meta-cell" style={{ borderRight: "0" }}>
            <span className="workorder-meta-label">Remark:</span>
            <span className="workorder-meta-value">{narration}</span>
          </div>
        </div>
      ) : null}

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons mr-actions">
        <DownloadButton
          tableData={invoiceTableData || mrTableData}
          fileName={selectedReportName || "material-requirement-form"}
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".mr-page-container"
        />
      </div>
    </div>
  );
};
